import { Country } from '@commenda/prisma';
import { LeveledJurisdiction } from '@commenda/types/src/corporation';
import { Country as CountryPkg, State as StatePkg } from 'country-state-city';
import { useMemo } from 'react';
// Import the module, not the package barrel: @commenda/api re-exports UI hooks, so the barrel
// would make the two packages import each other.
import { useAllJurisdictions } from '@commenda/api/business-entity/useAllJurisdictions';

const COUNTRY_LEVEL = 'adm0';

/** Every content jurisdiction, not only those a legal form is filed under. Ids are looked up, never parsed. */
export function useJurisdictionLookup() {
  const { data } = useAllJurisdictions();

  const byId = useMemo(
    () =>
      new Map(
        Object.values(data?.jurisdictions ?? {})
          .flatMap((forCountry) => Object.values(forCountry ?? {}))
          .filter((jurisdiction): jurisdiction is LeveledJurisdiction =>
            Boolean(jurisdiction?.id)
          )
          .map((jurisdiction) => [jurisdiction.id, jurisdiction] as const)
      ),
    [data]
  );

  // Keyed by country: TN is both Tennessee and Tamil Nadu.
  const byCountryAndCode = useMemo(
    () =>
      new Map(
        Object.entries(data?.jurisdictions ?? {}).flatMap(
          ([country, forCountry]) =>
            Object.values(forCountry ?? {})
              .filter((jurisdiction): jurisdiction is LeveledJurisdiction =>
                Boolean(jurisdiction?.id && jurisdiction.subNationalCode)
              )
              .map(
                (jurisdiction) =>
                  [
                    `${country}:${jurisdiction.subNationalCode}`,
                    jurisdiction.id,
                  ] as const
              )
        )
      ),
    [data]
  );

  return useMemo(() => {
    const idOf = (
      jurisdiction?: string | null,
      country?: Country | null
    ): string | null => {
      if (!jurisdiction) return null;
      if (byId.has(jurisdiction)) return jurisdiction;
      return country
        ? (byCountryAndCode.get(`${country}:${jurisdiction}`) ?? null)
        : null;
    };

    /** Whether the content calls it a country. False until the content has been read. */
    const isCountryLevel = (jurisdictionId?: string | null): boolean =>
      !!jurisdictionId && byId.get(jurisdictionId)?.level === COUNTRY_LEVEL;

    return {
      /** Until the content is read, nothing is country-level and a label is the id. */
      isLoaded: byId.size > 0,

      isCountryLevel,

      /** What to call a jurisdiction. Falls back to the id, which is at least stable and specific. */
      labelOf: (jurisdictionId?: string | null): string =>
        jurisdictionId
          ? (byId.get(jurisdictionId)?.displayName ?? jurisdictionId)
          : '',

      idOf,

      /** The country's name when it covers the country, else the jurisdiction's. Credentials may still hold a subdivision code. */
      placeOf: (
        jurisdiction: string | null | undefined,
        country: Country
      ): string => {
        const jurisdictionId = idOf(jurisdiction, country);
        if (!jurisdiction || isCountryLevel(jurisdictionId))
          return CountryPkg.getCountryByCode(country)?.name ?? country;
        if (jurisdictionId)
          return byId.get(jurisdictionId)?.displayName ?? jurisdictionId;
        return (
          StatePkg.getStateByCodeAndCountry(jurisdiction, country)?.name ||
          jurisdiction
        );
      },
    };
  }, [byId, byCountryAndCode]);
}
